import { Capacitor } from '@capacitor/core'
import { Health, type HealthDataType, type Workout } from '@capgo/capacitor-health'
import { Preferences } from '@capacitor/preferences'
import { deviceReadingsToBatch, sleepNightsByApp } from '@refuelled/domain'
import type { HealthBatch, HealthBatchResult } from '@refuelled/shared'
import { api } from '@/api/client'
import { toDateString } from '@/lib/dates'

/**
 * The phone's own health store (Health Connect on Android, HealthKit on
 * iOS) read on the device and pushed to `/sync/health` (M-72). Not a
 * provider (ADR-019): nothing to connect on a server, no tokens, no webhook.
 * The raw store never leaves the phone; what goes up is the day's figures.
 *
 * The watermark, the last instant read up to, lives in Capacitor
 * Preferences on this phone. A person's phone syncs that person only.
 */

export const PHONE_HEALTH_READS: readonly HealthDataType[] = [
  'steps',
  'distance',
  'calories',
  'sleep',
  'weight',
  'bodyFat',
  'restingHeartRate',
  'heartRate',
  'oxygenSaturation',
  'workouts',
]

const KEY_CONNECTED = 'health.connected'
const KEY_SINCE = 'health.since'
const KEY_LAST = 'health.last'
/**
 * Set once the last month has been read again on this build. Bumped when
 * sleep began going up per app, as a night and its naps (ADR-034), so every
 * stored night gets its per-app rows. Before that: Before this fix
 * a night recorded by two apps was added up twice; reading the last 30 days
 * once more puts every stored night right, since the server takes a resent
 * figure over the old one.
 */
const KEY_SLEEP_REREAD = 'health.reread.sleep-by-app-2026-10'

/** Without history permission the platform offers the last 30 days; ask for those. */
const FIRST_SYNC_DAYS = 30
const DAY_MS = 86_400_000

export interface PhoneHealthSummary {
  at: string
  days: number
  nights: number
  sessions: number
  alreadyKnown: number
  /**
   * Types Health Connect would not hand over.
   *
   * Carried rather than only logged, because a refused read looks exactly
   * like a device with nothing to report. Heart rate was refused for a whole
   * release and the only trace was a console warning nobody was reading.
   */
  refused: HealthDataType[]
}

export type PhoneHealthProvider = HealthBatch['provider']

export function phoneHealthProvider(): PhoneHealthProvider {
  return Capacitor.getPlatform() === 'ios' ? 'healthkit' : 'health_connect'
}

export async function phoneHealthAvailable(): Promise<{ available: boolean; reason: string | null }> {
  if (!Capacitor.isNativePlatform()) return { available: false, reason: null }
  try {
    const result = await Health.isAvailable()
    return { available: result.available, reason: result.available ? null : (result.reason ?? 'Health Connect is not on this phone') }
  } catch (err) {
    return { available: false, reason: (err as Error).message }
  }
}

async function pref(key: string): Promise<string | null> {
  return (await Preferences.get({ key })).value
}

export async function phoneHealthState(): Promise<{ connected: boolean; last: PhoneHealthSummary | null }> {
  const [connected, last] = await Promise.all([pref(KEY_CONNECTED), pref(KEY_LAST)])
  return { connected: connected === '1', last: last ? (JSON.parse(last) as PhoneHealthSummary) : null }
}

/** Ask the platform for read access; true when anything was granted. */
export async function phoneHealthConnect(): Promise<{ granted: HealthDataType[]; denied: HealthDataType[] }> {
  const status = await Health.requestAuthorization({ read: [...PHONE_HEALTH_READS] })
  const granted = status.readAuthorized ?? []
  if (granted.length > 0) await Preferences.set({ key: KEY_CONNECTED, value: '1' })
  return { granted, denied: status.readDenied ?? [] }
}

/** Stop reading; what was brought stays. Access itself is withdrawn in Health Connect's own settings. */
export async function phoneHealthDisconnect(): Promise<void> {
  await Promise.all([KEY_CONNECTED, KEY_SINCE, KEY_LAST].map((key) => Preferences.remove({ key })))
}

export async function openPhoneHealthSettings(): Promise<void> {
  await Health.openHealthConnectSettings()
}

async function allWorkouts(startDate: string, endDate: string): Promise<Workout[]> {
  const out: Workout[] = []
  let anchor: string | undefined
  for (let page = 0; page < 20; page += 1) {
    const result = await Health.queryWorkouts({ startDate, endDate, limit: 200, ascending: true, anchor })
    out.push(...result.workouts)
    if (!result.anchor || result.workouts.length === 0) break
    anchor = result.anchor
  }
  return out
}

async function daily(dataType: HealthDataType, startDate: string, endDate: string) {
  const result = await Health.queryAggregated({ dataType, startDate, endDate, bucket: 'day', aggregation: 'sum' })
  return result.samples.map((s) => ({ startDate: s.startDate, endDate: s.endDate, value: s.value }))
}

async function samples(dataType: HealthDataType, startDate: string, endDate: string, limit = 2000) {
  const result = await Health.readSamples({ dataType, startDate, endDate, limit, ascending: true })
  return result.samples.map((s) => ({
    startDate: s.startDate,
    endDate: s.endDate,
    value: s.value,
    sourceId: s.sourceId ?? null,
    stages: s.stages?.map((st) => ({ stage: st.stage, durationMinutes: st.durationMinutes })),
  }))
}

/**
 * Heart rate is the densest thing here: a band measuring every few minutes
 * writes hundreds of records a day, and a continuous one far more.
 *
 * The default 2,000 would quietly keep the oldest part of the window and
 * drop the rest, which would not look like an error and would bias the
 * derived resting figure (ADR-025) towards whichever hours survived.
 */
const HEART_RATE_LIMIT = 100_000

/**
 * A type that was refused reads as nothing rather than failing the whole
 * sync: one permission somebody declined should not cost them their steps.
 *
 * It records which type it was, so the refusal can be shown to somebody
 * instead of living only in a console warning.
 */
async function quietly<T>(
  dataType: HealthDataType,
  work: Promise<T[]>,
  refused: HealthDataType[],
): Promise<T[]> {
  try {
    return await work
  } catch (err) {
    console.warn('[health]', dataType, (err as Error).message)
    refused.push(dataType)
    return []
  }
}

/**
 * The reads this app wants that the platform has not granted.
 *
 * Adding a type to `PHONE_HEALTH_READS` does not ask anybody for it: the
 * request happens when somebody connects, and anyone who connected before
 * that release never sees a prompt. This is what makes the gap visible.
 */
export async function phoneHealthMissing(): Promise<HealthDataType[]> {
  if (!Capacitor.isNativePlatform()) return []
  try {
    const status = await Health.checkAuthorization({ read: [...PHONE_HEALTH_READS] })
    const granted = new Set(status.readAuthorized ?? [])
    return PHONE_HEALTH_READS.filter((type) => !granted.has(type))
  } catch {
    // Not knowing is not the same as knowing something is missing; a sync
    // that cannot check should still go ahead and read what it can.
    return []
  }
}

/**
 * Read everything since the last sync and post it. The window opens a day
 * before the watermark so a day's bucket that was partial last time is
 * read whole, and the server merges by (member, date, source).
 */
export async function phoneHealthSync(memberId: string): Promise<PhoneHealthSummary> {
  const now = new Date()
  const [sinceIso, reread] = await Promise.all([pref(KEY_SINCE), pref(KEY_SLEEP_REREAD)])
  const since = sinceIso && reread ? new Date(sinceIso) : new Date(now.getTime() - FIRST_SYNC_DAYS * DAY_MS)
  const from = new Date(since.getTime() - DAY_MS)
  from.setHours(0, 0, 0, 0)
  const startDate = from.toISOString()
  const endDate = now.toISOString()

  // Filled by `quietly` as each read either lands or is refused.
  const refused: HealthDataType[] = []
  const [steps, distance, activeCalories, sleep, weight, bodyFat, restingHeartRate, heartRate, oxygenSaturation, workouts] = await Promise.all([
    quietly('steps', daily('steps', startDate, endDate), refused),
    quietly('distance', daily('distance', startDate, endDate), refused),
    quietly('calories', daily('calories', startDate, endDate), refused),
    quietly('sleep', samples('sleep', new Date(from.getTime() - DAY_MS).toISOString(), endDate), refused),
    quietly('weight', samples('weight', startDate, endDate), refused),
    quietly('bodyFat', samples('bodyFat', startDate, endDate), refused),
    quietly('restingHeartRate', samples('restingHeartRate', startDate, endDate), refused),
    quietly('heartRate', samples('heartRate', startDate, endDate, HEART_RATE_LIMIT), refused),
    quietly('oxygenSaturation', samples('oxygenSaturation', startDate, endDate), refused),
    quietly('workouts', allWorkouts(startDate, endDate), refused),
  ])

  const batch = deviceReadingsToBatch(
    {
      steps,
      distance,
      activeCalories,
      sleep,
      weight,
      bodyFat,
      restingHeartRate,
      heartRate,
      oxygenSaturation,
      workouts: workouts.map((w) => ({
        platformId: w.platformId,
        workoutType: w.workoutType,
        startDate: w.startDate,
        endDate: w.endDate,
        duration: w.duration,
        totalEnergyBurned: w.totalEnergyBurned,
        totalDistance: w.totalDistance,
      })),
    },
    (iso) => toDateString(new Date(iso)),
  )

  // Sleep goes up per app, as each app's night and its naps (ADR-034).
  const sleepNights = sleepNightsByApp(sleep, (iso) => toDateString(new Date(iso))).map((n) => ({
    date: n.date,
    app: n.app,
    sleepMin: n.sleepMin,
    napMin: n.napMin,
    deepMin: n.deepMin,
    lightMin: n.lightMin,
    remMin: n.remMin,
    hasStages: n.hasStages,
  }))

  // Yesterday and today are still being written to; keep them in the next window.
  const provider = phoneHealthProvider()
  const totals: HealthBatchResult = { workoutsImported: 0, workoutsMerged: 0, metricsWritten: 0 }
  const chunks = Math.max(1, Math.ceil(Math.max(batch.metrics.length, batch.workouts.length) / 200))
  for (let i = 0; i < chunks; i += 1) {
    const body: HealthBatch = {
      memberId,
      provider,
      workouts: batch.workouts.slice(i * 200, (i + 1) * 200),
      metrics: batch.metrics.slice(i * 200, (i + 1) * 200),
      // All in the first post: the server clears a day's per-app nights
      // before writing them, so one day split across posts would lose half.
      sleepNights: i === 0 ? sleepNights : [],
    }
    if (body.workouts!.length === 0 && body.metrics!.length === 0 && body.sleepNights!.length === 0) continue
    const result = await api.post<HealthBatchResult>('/sync/health', body)
    totals.workoutsImported += result.workoutsImported
    totals.workoutsMerged += result.workoutsMerged
    totals.metricsWritten += result.metricsWritten
  }

  const summary: PhoneHealthSummary = {
    at: now.toISOString(),
    days: totals.metricsWritten,
    nights: new Set(sleepNights.map((n) => n.date)).size,
    sessions: totals.workoutsImported,
    alreadyKnown: totals.workoutsMerged,
    // Steps is read twice; the same refusal should be said once.
    refused: [...new Set(refused)],
  }
  await Preferences.set({ key: KEY_SINCE, value: now.toISOString() })
  await Preferences.set({ key: KEY_SLEEP_REREAD, value: now.toISOString() })
  await Preferences.set({ key: KEY_LAST, value: JSON.stringify(summary) })
  return summary
}
